import { zodResolver } from '@hookform/resolvers/zod';
import {
  DateInput,
  Dialog,
  Form,
  FormButton,
  useNotifications
} from '@ijoin/components';
import { usePostMapOptOut } from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ApiError } from '../types';

type FormValues = {
  optOutDate: string;
};

export const MapOptOutDialog: FC<{
  onClose: () => void;
  participantName: string;
  planEnrollmentId: string;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const methods = useForm<FormValues>({
    defaultValues: { optOutDate: new Date().toISOString() },
    mode: 'all',
    resolver: zodResolver(
      z.object({ optOutDate: z.string().min(1, 'Opt-out date is required') })
    )
  });

  const mapOptOut = usePostMapOptOut<ApiError>({
    mutation: {
      onError: error =>
        notifications.error(error.message || 'Failed to opt out of MAP'),
      onSuccess: () => {
        queryClient.invalidateQueries();
        notifications.success('Participant opted out of MAP');
        props.onClose();
      }
    }
  });

  const onSubmit = (data: FormValues) => {
    mapOptOut.mutate({
      data: { optOutDate: data.optOutDate },
      planEnrollmentId: props.planEnrollmentId
    });
  };

  return (
    <Dialog
      data-testid='mapOptOut'
      fullWidth
      isOpen
      maxWidth='xs'
      onClose={props.onClose}>
      <Form
        methods={methods}
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>Opt Out of MAP</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DialogContentText>
              {`${props.participantName} will be opted out of the managed account program.`}
            </DialogContentText>
            <DateInput
              data-testid='mapOptOutDate'
              fullWidth
              label='Opt-Out Date'
              name='optOutDate'
              required
              size='small'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelMapOptOut'
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            color='error'
            data-testid='saveMapOptOut'
            triggerValidation
            variant='contained'>
            Opt Out
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

MapOptOutDialog.displayName = 'MapOptOutDialog';
